/**
 * Phase 1: InterpretationPayload → ReadingDocument fact outline (spec §6, §9).
 * Facts only — no prose essays.
 */

import { hashPayload } from "./hash.js";

/** @type {Record<string, string>} */
const SECTION_TITLES = Object.freeze({
  soulEngine: "The Soul Engine — Pluto & The Evolutionary Release Valve",
  bridge: "The Nodal Axis — The Bridge of the Evolutionary Arc & Rulers",
  skippedSteps: "Unresolved: Skipped Steps",
  faces: "The Four Faces of the Self (The Four Angles)",
  catalyst: "Catalyst: Planets, Angles & Chiron on the North Node",
  pastMastery: "Past Mastery: Planets, Angles & Chiron on the South Node",
  saturnChiron: "Structure & Gift: Saturn and Chiron",
  higherOctave: "Higher Intelligence: Uranus and Neptune",
  crucible: "Foundational Crucibles: Core Clusters & Elemental Balance",
  moon: "Daily Life: Moon — Emotional Integration",
  sovereignty: "Sovereignty (Pathways to Freedom)",
  synthesis: "Core Overview & Conscious Choice",
});

/**
 * @param {object} payload - InterpretationPayload
 * @param {{ generatedAt?: string }} [options]
 * @returns {object} ReadingDocument
 */
export function buildOutlineReading(payload, options = {}) {
  const plan = payload.sectionPlan || Object.keys(SECTION_TITLES);
  const sections = plan.map((id) => sectionFor(id, payload));

  return {
    schemaVersion: 1,
    displayName: payload.meta?.displayName ?? null,
    source: "outline",
    generatedAt: options.generatedAt || new Date().toISOString(),
    payloadHash: hashPayload(payload),
    sections,
  };
}

/**
 * @param {string} id
 * @param {object} payload
 */
function sectionFor(id, payload) {
  const title = SECTION_TITLES[id] || id;
  const writer = WRITERS[id];
  if (!writer) {
    return { id, title, markdown: "_No outline writer for this section._" };
  }
  const { markdown, skipped } = writer(payload);
  const section = { id, title, markdown };
  if (skipped) section.skipped = true;
  return section;
}

function bullet(label, value) {
  return `- **${label}:** ${value}`;
}

function formatBody(b) {
  if (!b) return "—";
  const h = b.house != null ? ` · house ${b.house}` : "";
  const rx = b.isRetrograde ? " Rx" : "";
  const deg =
    b.degree != null && b.minute != null
      ? ` ${pad2(b.degree)}°${pad2(b.minute)}'`
      : "";
  return `${b.name || ""}${deg} ${b.signName || ""}${h}${rx}`.trim();
}

function formatPpp(ppp) {
  if (!ppp) return "—";
  const h = ppp.house != null ? ` · house ${ppp.house}` : "";
  return `${pad2(ppp.degree)}°${pad2(ppp.minute)}' ${ppp.signName}${h}`;
}

function formatHits(hits) {
  if (!hits || !hits.length) return "none";
  return hits
    .map((h) => {
      const house = h.house != null ? ` H${h.house}` : "";
      return `${h.name} (${h.orb.toFixed(2)}°${house})`;
    })
    .join("; ");
}

function formatRuler(planet, body) {
  if (!planet) return "—";
  if (!body) return planet;
  return `${planet} → ${formatBody({ ...body, name: planet })}`;
}

function pad2(n) {
  return String(Math.floor(Number(n))).padStart(2, "0");
}
/** @type {Record<string, (p: object) => { markdown: string, skipped?: boolean }>} */
const WRITERS = {
  soulEngine(p) {
    const pl = p.derived.pluto;
    const lines = [
      bullet("Pluto", formatBody(pl.body)),
      bullet("PPP (Pluto + 180°)", formatPpp(pl.ppp)),
      bullet("Bodies conjunct Pluto", formatHits(pl.conjunct)),
    ];
    return { markdown: lines.join("\n") };
  },

  bridge(p) {
    const nn = p.nodes.north;
    const sn = p.nodes.south;
    const nr = p.derived.nodalRulers;
    const lines = [
      bullet("North Node", formatBody(nn)),
      bullet("South Node", formatBody(sn)),
      bullet(
        "North Node ruler",
        formatRuler(nr.northRuler.planet, nr.northRuler.body)
      ),
      bullet(
        "South Node ruler",
        formatRuler(nr.southRuler.planet, nr.southRuler.body)
      ),
    ];
    return { markdown: lines.join("\n") };
  },

  catalyst(p) {
    const c = p.derived.catalyst;
    const lines = [
      bullet("On North Node (within orb)", formatHits(c.onNorthNode)),
    ];
    if (c.proxyHint) {
      lines.push(bullet("Proxy hint (no direct hit)", c.proxyHint));
    }
    return { markdown: lines.join("\n") };
  },

  pastMastery(p) {
    return {
      markdown: bullet(
        "On South Node (within orb)",
        formatHits(p.derived.pastMastery.onSouthNode)
      ),
    };
  },

  skippedSteps(p) {
    return {
      markdown: bullet(
        "Square nodal axis",
        formatHits(p.derived.skippedSteps.squaresToNodes)
      ),
    };
  },

  saturnChiron(p) {
    return {
      markdown: [
        bullet("Saturn", formatBody(p.derived.saturn)),
        bullet("Chiron", formatBody(p.derived.chiron)),
      ].join("\n"),
    };
  },

  higherOctave(p) {
    return {
      markdown: [
        bullet("Uranus", formatBody(p.derived.uranus)),
        bullet("Neptune", formatBody(p.derived.neptune)),
      ].join("\n"),
    };
  },

  faces(p) {
    const a = p.angles;
    const h = p.derived.angularHits;
    return {
      markdown: [
        bullet("ASC — Personal Face", formatBody(a.asc)),
        bullet("  conjunct ASC", formatHits(h.asc)),
        bullet("DSC — Relational Face", formatBody(a.dsc)),
        bullet("  conjunct DSC", formatHits(h.dsc)),
        bullet("MC — Public Face", formatBody(a.mc)),
        bullet("  conjunct MC", formatHits(h.mc)),
        bullet("IC — Private Face", formatBody(a.ic)),
        bullet("  conjunct IC", formatHits(h.ic)),
      ].join("\n"),
    };
  },

  crucible(p) {
    const st = p.derived.stelliums || [];
    if (!st.length) {
      return {
        markdown: "_No stellium / cluster rule fired for this chart._",
        skipped: true,
      };
    }
    const lines = st.map((s) => {
      const span =
        s.spanDegrees != null ? `, span ${s.spanDegrees.toFixed(1)}°` : "";
      return `- **${s.mode} ${s.key}:** ${s.members.join(", ")}${span}`;
    });
    return { markdown: lines.join("\n") };
  },

  moon(p) {
    return { markdown: bullet("Moon", formatBody(p.derived.moon)) };
  },

  sovereignty(p) {
    const f = p.derived.sovereigntyFlags || {};
    return {
      markdown: [
        bullet("ancestralWeight", f.ancestralWeight ? "true" : "false"),
        bullet("enmeshmentRisk", f.enmeshmentRisk ? "true" : "false"),
        bullet("groundedPath", f.groundedPath ? "true" : "false"),
      ].join("\n"),
    };
  },

  synthesis(p) {
    const pl = p.derived.pluto;
    const nn = p.nodes.north;
    const sn = p.nodes.south;
    return {
      markdown: [
        "- **Growth chosen (facts):** integrate PPP release + North Node path.",
        bullet("  Pluto / pressure", formatBody(pl.body)),
        bullet("  PPP / release", formatPpp(pl.ppp)),
        bullet("  North Node aim", formatBody(nn)),
        bullet("  South Node fallback", formatBody(sn)),
        "- **Not chosen:** remaining in SN / Pluto compulsion without PPP or NN work (prose later).",
      ].join("\n"),
    };
  },
};

